import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CalendarClock, ExternalLink, ReceiptText, Repeat, Search } from 'lucide-react';
import { DataList, DataListRow } from '@/components/data-list';
import { EmptyState } from '@/components/empty-state';
import { EntityAvatar } from '@/components/entity-avatar';
import { Input } from '@/components/input';
import { PageHeader } from '@/components/page-header';
import { SkeletonList } from '@/components/skeleton';
import { StatusPill } from '@/components/status-dot';
import { Tabs } from '@/components/tabs';
import { cn } from '@/lib/cn';
import { dollars, formatDate } from '@/lib/format';
import { usePendingReceiptsCount } from '@/lib/subscription-charges';
import {
  intervalLabel,
  subscriptionStatusLabel,
  subscriptionStatusTone,
  useAllSubscriptions,
  useSubscriptionSummary,
  type SubscriptionSegment,
} from '@/lib/subscriptions';
import { ReceiptsQueue } from '@/routes/subscription-receipts';
import type { SubscriptionData, SubscriptionSummaryData } from '@/types/api';

/**
 * Everything recurring in one place: the subscriptions themselves, and the
 * renewal receipts waiting to go out. Stripe stays the system of record, so
 * this page reads; changes happen in Stripe and sync back.
 */
export function SubscriptionsIndex() {
  const pendingReceipts = usePendingReceiptsCount(true).data ?? 0;

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Repeat}
        title="Subscriptions"
        subtitle="Every recurring plan, mirrored from Stripe. Change or cancel them in Stripe and this page follows."
      />
      <Tabs
        paramName="tab"
        tabs={[
          { key: 'subscriptions', label: 'Subscriptions', icon: Repeat, content: <SubscriptionsList /> },
          {
            key: 'receipts',
            label: pendingReceipts > 0 ? `Receipts (${pendingReceipts})` : 'Receipts',
            icon: ReceiptText,
            content: <ReceiptsQueue />,
          },
        ]}
      />
    </div>
  );
}

const SEGMENTS: Array<{ key: SubscriptionSegment; label: string }> = [
  { key: 'active', label: 'Active' },
  { key: 'at_risk', label: 'At risk' },
  { key: 'ended', label: 'Ended' },
  { key: 'all', label: 'All' },
];

function isSegment(value: string | null): value is SubscriptionSegment {
  return SEGMENTS.some((s) => s.key === value);
}

function SubscriptionsList() {
  const [params, setParams] = useSearchParams();
  const segmentParam = params.get('segment');
  const segment: SubscriptionSegment = isSegment(segmentParam) ? segmentParam : 'active';
  const [search, setSearch] = useState('');
  const summary = useSubscriptionSummary();
  const { data, isLoading } = useAllSubscriptions(segment);

  // Kept in the URL so the dashboard can link straight to what's at risk.
  function setSegment(next: SubscriptionSegment) {
    const updated = new URLSearchParams(params);
    updated.set('segment', next);
    setParams(updated, { replace: true });
  }

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return data ?? [];
    return (data ?? []).filter((s) =>
      [s.client_name, s.display_name].some((v) => v?.toLowerCase().includes(needle)),
    );
  }, [data, search]);

  return (
    <div className="space-y-4">
      <SummaryStrip summary={summary.data} onAtRisk={() => setSegment('at_risk')} />

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 rounded-md bg-[var(--color-surface-muted)] p-1" role="tablist" aria-label="Filter subscriptions">
          {SEGMENTS.map((s) => (
            <button
              key={s.key}
              type="button"
              role="tab"
              aria-selected={segment === s.key}
              onClick={() => setSegment(s.key)}
              className={cn(
                'rounded px-3 py-1.5 text-sm font-medium',
                segment === s.key
                  ? 'bg-[var(--color-surface-elevated)] text-[var(--color-foreground)] shadow-sm'
                  : 'text-[var(--color-foreground-muted)] hover:text-[var(--color-foreground)]',
              )}
            >
              {s.label}
              {s.key === 'at_risk' && summary.data && summary.data.at_risk_count > 0 ? (
                <span className="ml-1.5 tabular-nums text-[var(--color-warning)]">{summary.data.at_risk_count}</span>
              ) : null}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[var(--color-foreground-subtle)]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search client or plan"
            className="pl-9"
          />
        </div>
      </div>

      {isLoading ? (
        <SkeletonList rows={4} />
      ) : rows.length === 0 ? (
        <EmptyState icon={Repeat} {...emptyCopy(segment, search)} />
      ) : (
        <DataList>
          {rows.map((sub) => (
            <SubscriptionRow key={sub.id} sub={sub} />
          ))}
        </DataList>
      )}
    </div>
  );
}

function emptyCopy(segment: SubscriptionSegment, search: string): { title: string; description: string } {
  if (search.trim()) {
    return { title: 'No matches', description: 'Nothing in this view matches that client or plan.' };
  }
  return {
    active: {
      title: 'No active subscriptions',
      description: 'Create a subscription in Stripe and it shows up here once it syncs.',
    },
    at_risk: {
      title: 'Nothing at risk',
      description: 'No failed payments, paused plans or scheduled cancellations.',
    },
    ended: { title: 'Nothing has ended', description: 'Canceled subscriptions are listed here.' },
    all: {
      title: 'No subscriptions yet',
      description: 'Create a subscription in Stripe and it shows up here once it syncs.',
    },
  }[segment];
}

function SummaryStrip({
  summary,
  onAtRisk,
}: {
  summary: SubscriptionSummaryData | undefined;
  onAtRisk: () => void;
}) {
  const tiles: Array<{ label: string; value: string; sub?: string; accent?: boolean; warn?: boolean; onClick?: () => void }> = [
    {
      label: 'MRR',
      value: summary ? dollars(summary.mrr_cents) : '—',
      sub: summary ? `${summary.active_count} active` : undefined,
      accent: true,
    },
    { label: 'ARR', value: summary ? dollars(summary.arr_cents) : '—', sub: 'MRR × 12' },
    {
      label: 'Renewing in 30 days',
      value: summary ? dollars(summary.renewing_cents) : '—',
      sub: summary ? `${summary.renewing_count} ${summary.renewing_count === 1 ? 'renewal' : 'renewals'}` : undefined,
    },
    {
      label: 'At risk',
      value: summary ? String(summary.at_risk_count) : '—',
      sub: summary && summary.at_risk_count > 0 ? `${dollars(summary.at_risk_mrr_cents)} a month` : 'All healthy',
      warn: (summary?.at_risk_count ?? 0) > 0,
      onClick: summary && summary.at_risk_count > 0 ? onAtRisk : undefined,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {tiles.map((t) => {
        const body = (
          <>
            <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{t.label}</div>
            <div className={cn('mt-1.5 text-2xl font-semibold tabular-nums tracking-tight', t.warn && 'text-[var(--color-warning)]')}>
              {t.value}
            </div>
            {t.sub ? <div className="mt-0.5 text-xs text-muted-foreground">{t.sub}</div> : null}
          </>
        );
        const className = cn(
          'rounded-xl border bg-card p-4 text-left shadow-xs',
          t.accent ? 'border-primary/20' : 'border-border/70',
        );
        return t.onClick ? (
          <button key={t.label} type="button" onClick={t.onClick} className={cn(className, 'transition-colors hover:border-primary/40')}>
            {body}
          </button>
        ) : (
          <div key={t.label} className={className}>
            {body}
          </div>
        );
      })}
    </div>
  );
}

/** When it bills next, or why it won't. */
function renewalText(sub: SubscriptionData): string | null {
  if (!sub.counts_toward_mrr && sub.canceled_at) return `Ended ${formatDate(sub.canceled_at)}`;
  if (!sub.current_period_end) return null;
  if (sub.cancel_at_period_end) return `Ends ${formatDate(sub.current_period_end)}`;
  if (sub.status === 'past_due' || sub.status === 'unpaid') return `Payment due since ${formatDate(sub.current_period_end)}`;
  if (sub.counts_toward_mrr) return `Renews ${formatDate(sub.current_period_end)}`;
  return null;
}

const RECEIPT_STATE: Record<string, string> = {
  pending_review: 'receipt to send',
  sent: 'receipt sent',
  dismissed: 'no receipt',
};

function SubscriptionRow({ sub }: { sub: SubscriptionData }) {
  const navigate = useNavigate();
  const renewal = renewalText(sub);
  const linked = sub.client_id !== null;

  return (
    <DataListRow
      interactive={linked}
      onClick={linked ? () => navigate(`/clients/${sub.client_id}?tab=subscriptions`) : undefined}
    >
      <EntityAvatar name={sub.client_name ?? '?'} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="truncate font-medium text-foreground">{sub.client_name ?? 'No client linked'}</span>
          <span className="truncate text-xs text-muted-foreground">{sub.display_name}</span>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          <span className="tabular-nums">
            {dollars(sub.amount_cents)} {intervalLabel(sub.interval, sub.interval_count).toLowerCase()}
          </span>
          {renewal ? (
            <>
              <span aria-hidden>·</span>
              <span className={cn(sub.is_at_risk && 'text-[var(--color-warning)]')}>
                {sub.is_at_risk ? <AlertTriangle className="mr-1 inline size-3 align-[-2px]" /> : <CalendarClock className="mr-1 inline size-3 align-[-2px]" />}
                {renewal}
              </span>
            </>
          ) : null}
          {sub.last_charge?.paid_at ? (
            <>
              <span aria-hidden>·</span>
              <span>
                Last charged {formatDate(sub.last_charge.paid_at)}
                {RECEIPT_STATE[sub.last_charge.status] ? `, ${RECEIPT_STATE[sub.last_charge.status]}` : ''}
              </span>
            </>
          ) : null}
        </div>
      </div>
      <div className="hidden shrink-0 text-right sm:block">
        <div className="font-semibold tabular-nums">{dollars(sub.monthly_equivalent_cents)}</div>
        <div className="text-xs text-muted-foreground">a month</div>
      </div>
      <StatusPill tone={sub.cancel_at_period_end && sub.counts_toward_mrr ? 'warning' : subscriptionStatusTone(sub.status)}>
        {subscriptionStatusLabel(sub)}
      </StatusPill>
      <a
        href={sub.stripe_url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        aria-label={`Open ${sub.display_name} in Stripe`}
        title="Open in Stripe"
        className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus:opacity-100 group-hover/row:opacity-100"
      >
        <ExternalLink className="size-4" />
      </a>
    </DataListRow>
  );
}
